import { estadoPersonaInfo } from '@lib/estado-persona';
import type { TonoEstado } from '@lib/estado-legajo';

const TONO_CLASS: Record<TonoEstado, string> = {
  success: 'status status-success',
  info: 'status status-info',
  warning: 'status status-warning',
  error: 'status status-error',
  neutral: 'status',
};

/**
 * `true` si la fecha (DD/MM/AAAA) es posterior a hoy. Devuelve `false` ante
 * cualquier formato raro: preferimos mostrar "De baja" a secas antes que
 * afirmar que una baja está programada cuando no pudimos leer la fecha.
 */
function esFutura(fecha: string): boolean {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(fecha.trim());
  if (!m) return false;
  const [, d, mes, a] = m;
  const objetivo = new Date(Number(a), Number(mes) - 1, Number(d));
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  return objetivo.getTime() > hoy.getTime();
}

/**
 * Badge del estado consolidado de la PERSONA (activa / de baja / bloqueada /
 * con error). Análogo a `EstadoLegajoBadge` pero para el asociado. `motivo`
 * (opcional) se agrega al tooltip para dar el detalle (ej. motivo de la baja o
 * del bloqueo).
 *
 * `vigencia` es la fecha desde la que rige la baja. Se muestra porque una baja
 * mandada a Binary NO impacta en el momento: queda una solicitud con vigencia
 * futura y la persona sigue con cobertura hasta esa fecha. Sin la fecha, el
 * badge decía "De baja" desde el envío y no había manera de saber si la persona
 * ya dejó de tener cobertura o la deja el mes que viene.
 */
export function EstadoPersonaBadge({
  estado,
  motivo,
  vigencia,
  size = 11,
}: {
  estado: string;
  motivo?: string;
  vigencia?: string;
  size?: number;
}) {
  const info = estadoPersonaInfo(estado);
  const neutral = info.tono === 'neutral';
  // La vigencia solo tiene sentido sobre una baja; en bloqueada/error se ignora.
  const conVigencia = estado === 'baja' && !!vigencia;
  const pendiente = conVigencia && esFutura(vigencia);

  const etiqueta = pendiente ? 'Baja programada' : info.label;
  const partes = [etiqueta, motivo, conVigencia ? `vigencia ${vigencia}` : ''].filter(Boolean);
  const label = partes.join(' · ');

  const detalle = pendiente
    ? `La baja ya está cargada en Binary pero rige desde el ${vigencia}: hasta esa fecha la persona conserva la cobertura.`
    : conVigencia
      ? `Rige desde el ${vigencia}.`
      : '';
  const title = [info.descripcion, motivo, detalle].filter(Boolean).join(' — ');

  return (
    <span
      className={TONO_CLASS[info.tono]}
      title={title}
      style={{
        fontSize: size,
        cursor: 'help',
        whiteSpace: 'nowrap',
        textTransform: 'none',
        ...(neutral
          ? { background: 'var(--color-surface-accent, #eef1f5)', color: 'var(--color-element-mid)' }
          : {}),
      }}
    >
      {label}
    </span>
  );
}
